import { kebabCase, memoize } from 'lodash-es'
import { parseToRgb } from 'polished'
import { css } from 'src/lib/emotion'

import { colors as themeColors } from '../tailwind'

export const classes = (condition: any, ifClasses: string | string[], elseClasses = '') =>
  condition ? (Array.isArray(ifClasses) ? ifClasses.join(' ') : ifClasses) : elseClasses

export const hasPosition = (el: Element) => getComputedStyle(el).position !== 'static'

type PropName = 'border' | 'bg' | 'text'

const getTailwindClass = memoize(
  (name: PropName, value: string, variant?: string) => {
    return variant ? `${kebabCase(variant)}:${name}-${value}` : `${name}-${value}`
  },
  (name: PropName, value: string, variant = 'none') => name + value + variant
)

const getCssClass = memoize(
  (name: PropName, value: string, variant = 'none') => {
    const cssProp = {
      border: 'border-color',
      bg: 'background-color',
      text: 'color',
    }[name]
    const { red, green, blue } = parseToRgb(value)
    const customProp = `--tw-${name}-opacity`
    value = `rgba(${red}, ${green}, ${blue}, var(${customProp}))` // Support Tailwind's various -opacity classes
    const style = {
      hover: `
      &:hover {
        ${customProp}: 1;
        ${cssProp}: ${value};
      }`,
      groupHover: `
      .group:hover & {
        ${customProp}: 1;
        ${cssProp}: ${value};
      }`,
      none: `
      ${customProp}: 1;
      ${cssProp}: ${value};
      `,
    }
    return css`
      ${style[variant]}
    `
  },
  (name: PropName, value: string, variant = 'none') => name + value.toLowerCase() + variant
)

export const colorClasses = memoize(
  (
    colors: { [name in PropName]?: string },
    variants?: {
      hover?: { [name in PropName]?: string }
      groupHover?: { [name in PropName]?: string }
    }
  ) => {
    const colorClasses: string[] = []
    for (const [name, value] of Object.entries(colors || {})) {
      if (value) {
        colorClasses.push(
          value in themeColors ? getTailwindClass(name as PropName, value) : getCssClass(name as PropName, value)
        )
      }
    }
    for (const [variant, colors] of Object.entries(variants || {})) {
      for (const [name, value] of Object.entries(colors)) {
        if (value) {
          colorClasses.push(
            value in themeColors
              ? getTailwindClass(name as PropName, value, variant)
              : getCssClass(name as PropName, value, variant)
          )
        }
      }
    }
    return colorClasses.join(' ')
  },
  (...args) => JSON.stringify(args)
)
